import {useAxios} from "../utils/hooks";
import {Button, Col, Form, Input, Row, Select, Spin, List, Popconfirm, Tooltip} from "antd";
import useSafeState from "../utils/safe-state";
import React, {useEffect, useState} from "react";
import {openNotification} from "../utils/openNotification";
import {Trans} from "@lingui/macro";
import {DeleteOutlined, EditOutlined, PlusOutlined} from "@ant-design/icons";
import ButtonWithIcon from "../components/buttons/button-with-icon";

interface Interface{
    data?: any;
    mode?: 'Edit' | 'Add new';
    onClose?: () => void;
    itemId?: number;
}

const ContactDrawer = (props: Interface) => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const config = {headers: {'Content-Type': 'application/json'}};
    const [form] = Form.useForm();
    const [loading, setLoading] = useSafeState(false);
    const [contacts, setContacts] = useSafeState<any[]>([]);
    const [viewMode, setViewMode] = useState<'list' | 'form'>('list');
    const [editingContact, setEditingContact] = useState<any>(null);

    const itemId = props.itemId || (props.data && props.data.itemId);

    useEffect(() => {
        if (itemId) {
            fetchContacts();
        } else {
            if (props.mode === 'Edit' && props.data && props.data.id) {
                setEditingContact(props.data);
                setViewMode('form');
            }
        }
    }, [itemId, props.mode, props.data]);

    const fetchContacts = () => {
        if (axiosInstance.current && itemId) {
            setLoading(true);
            axiosInstance.current.post('/resource/contacts/page', {
                query: { itemId: itemId },
                start: 0,
                length: 100,
                direction: 'ASC',
                sorted_field: 'id'
            })
            .then(response => {
                setContacts(response.data.data || []);
            })
            .catch(error => {
                console.error(error);
                openNotification('error', 'Failed to fetch contacts');
            })
            .finally(() => {
                setLoading(false);
            });
        }
    };

    const handleEdit = (values: any) => {
        if (axiosInstance.current != null) {
            setLoading(true);
            const contactData: any = {
                id: editingContact.id,
                type: values.type,
                name: values.name,
                postalAddress: values.postalAddress,
                emailAddress: values.emailAddress,
                shippingAddress: values.shippingAddress,
            };
            
            if (itemId) {
                contactData.item = { id: itemId };
            } else {
                contactData.supplier = editingContact.supplier || { code: localStorage.getItem('supplierCode') };
            }
            
            axiosInstance.current
                .put(
                    '/resource/contacts/' + encodeURIComponent(editingContact.id),
                    contactData,
                    config,
                )
                .then(() => {
                    openNotification('success', 'Contact successfully updated.');
                    if (itemId) {
                        fetchContacts();
                        setViewMode('list');
                    } else if (props.onClose) {
                        props.onClose();
                    }
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const handleFinish = (values: any) => {
        if (axiosInstance.current != null) {
            setLoading(true);
            const contactData: any = {
                type: values.type,
                name: values.name,
                postalAddress: values.postalAddress,
                emailAddress: values.emailAddress,
                shippingAddress: values.shippingAddress,
            };
            
            if (itemId) {
                contactData.item = { id: itemId };
            } else {
                const supplierCode = localStorage.getItem('supplierCode');
                contactData.supplier = { code: supplierCode };
            }
            
            axiosInstance.current
                .post(
                    '/resource/contacts',
                    contactData,
                    config,
                )
                .then(() => {
                    openNotification('success', 'Contact successfully created.');
                    if (itemId) {
                        fetchContacts();
                        setViewMode('list');
                    } else if (props.onClose) {
                        props.onClose();
                    }
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    const handleDelete = (id: number) => {
        if (axiosInstance.current) {
            setLoading(true);
            axiosInstance.current.delete(`/resource/contacts/id?id=${id}`)
                .then(() => {
                    openNotification('success', 'Contact deleted');
                    fetchContacts();
                })
                .catch(error => openNotification('error', error))
                .finally(() => setLoading(false));
        }
    };

    useEffect(() => {
        if (viewMode === 'form') {
            form.resetFields();
            if (editingContact) {
                form.setFieldsValue(editingContact);
            }
        }
    }, [viewMode, editingContact]);

    if (viewMode === 'list' && itemId) {
        return (
            <Spin spinning={loading}>
                <div style={{ marginBottom: 16, textAlign: 'right' }}>
                    <Button type="primary" onClick={() => {
                        setEditingContact(null);
                        setViewMode('form');
                    }}>
                        <ButtonWithIcon icon={<PlusOutlined />} text={<Trans>Add Contact</Trans>} />
                    </Button>
                </div>
                <List
                    itemLayout="horizontal"
                    dataSource={contacts}
                    renderItem={item => (
                        <List.Item
                            actions={[
                                <Tooltip title={<Trans>Edit</Trans>}>
                                    <Button 
                                        type="link" 
                                        icon={<EditOutlined />} 
                                        onClick={() => {
                                            setEditingContact(item);
                                            setViewMode('form');
                                        }}
                                    />
                                </Tooltip>,
                                <Popconfirm
                                    title={<Trans>Are you sure delete this contact?</Trans>}
                                    onConfirm={() => handleDelete(item.id)}
                                    okText={<Trans>Yes</Trans>}
                                    cancelText={<Trans>No</Trans>}
                                >
                                    <Button type="link" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                            ]}
                        >
                            <List.Item.Meta
                                title={item.name}
                                description={
                                    <div>
                                        <div><Trans>Type</Trans>: {item.type}</div>
                                        <div><Trans>Email</Trans>: {item.emailAddress}</div>
                                        <div><Trans>Address</Trans>: {item.postalAddress}</div>
                                    </div>
                                }
                            />
                        </List.Item>
                    )}
                />
            </Spin>
        );
    }

    return (
        <Form
            id="newContact"
            layout='vertical'
            form={form}
            onFinish={(values) => (editingContact ? handleEdit(values) : handleFinish(values))}
        >
            <Spin spinning={loading}>
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="type"
                            label={<Trans>Type</Trans>}
                            rules={[{
                                required: true,
                                message: <Trans>Please select the type</Trans>
                            }]}
                        >
                            <Select placeholder={<Trans>Select type</Trans>}>
                                <Select.Option value="DEPARTURE">
                                    <Trans>DEPARTURE</Trans>
                                </Select.Option>
                                <Select.Option value="ARRIVAL">
                                    <Trans>ARRIVAL</Trans>
                                </Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="name"
                            label={<Trans>Name of business or person</Trans>}
                            rules={[{
                                required: true,
                                message: <Trans>Please enter the name</Trans>
                            }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="postalAddress"
                            label={<Trans>Postal Address</Trans>}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="emailAddress"
                            label={<Trans>Email Address</Trans>}
                            rules={[{
                                type: "email",
                                message: <Trans>Please enter a valid email address</Trans>
                            }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="shippingAddress"
                            label={<Trans>Shipping Address</Trans>}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
            </Spin>

            <div style={{ marginTop: 16, textAlign: 'right' }}>
                {itemId && (
                    <Button style={{ marginRight: 8 }} onClick={() => setViewMode('list')}>
                        <Trans>Cancel</Trans>
                    </Button>
                )}
                <Button
                    loading={loading}
                    htmlType="submit"
                    type="primary"
                >
                    <Trans>Save</Trans>
                </Button>
            </div>
        </Form>
    );

}
export default ContactDrawer;
